// @flow
import * as React from 'react';
import Modal from 'components/Modal';
import { Set } from 'immutable';
import AsyncValues, { type AsyncValueType } from 'lib/async-value';
import { Alert } from 'components/alerts';
import { Button, ButtonGroup } from 'components';
import classnames from 'classnames';

const Tchyns = {
  recipientCell: 'pv1 b--light-gray',
  recipientLabel: 'db mv1 mid-gray',
};

const Recipient = (props: { label: string, id: string, children?: any }) => {
  return (
    <td className={Tchyns.recipientCell} data-translate={false}>
      <label className={Tchyns.recipientLabel} htmlFor={props.id}>
        {props.children}
        <span className="ml3">{props.label}</span>
      </label>
    </td>
  );
};

Recipient.Empty = () => <td>&nbsp;</td>;

type Props = {
  title: string,
  onSubmit: () => void,
  onClose: () => void,
  userProfiles: Array<*>,
  currentAlertConfig: *,
  changeStatus: AsyncValueType<*>,
};
type State = {
  alertedUsers: Set<string>,
  batch: boolean,
};

class ChannelAlert extends React.PureComponent<Props, State> {
  state = {
    alertedUsers: Set(
      this.props.currentAlertConfig.recipients.map(r => r.profile_id)
    ),
    batch: this.props.currentAlertConfig.batch || false,
  };

  handleRecipientChange = (
    // e: Event & {
    //   currentTarget: HTMLInputElement & { value: string, checked: boolean },
    // }
    e: SyntheticEvent<HTMLInputElement>
  ) => {
    const { value: sIndex, checked: alerted } = e.currentTarget;
    const index = Number(sIndex);
    const item = this.props.userProfiles[index];

    item &&
      this.setState(({ alertedUsers }) => ({
        alertedUsers: alerted
          ? alertedUsers.add(item.profile_id)
          : alertedUsers.remove(item.profile_id),
      }));
  };

  handleBulkRecipientChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const alerted = e.currentTarget.checked;
    const { userProfiles } = this.props;

    this.setState({
      alertedUsers: alerted ? Set(userProfiles.map(p => p.profile_id)) : Set(),
    });
  };

  handleSubmit = () => {
    const updatedRecipientlist = this.state.alertedUsers
      .map(profile_id => ({ profile_id }))
      .toArray();

    this.props.onSubmit({
      ...this.props.currentAlertConfig,
      recipients: updatedRecipientlist,
      batch: this.state.batch,
    });
  };

  renderRecepients() {
    const { userProfiles } = this.props;
    const { alertedUsers } = this.state;

    const rows = userProfiles.reduce((acc, _, index, list) => {
      if (index % 2 === 0) {
        let rowKey = '';
        const rowItems = list.slice(index, index + 2).map((item, rIndex) => {
          if (item) {
            const localIndex = index + rIndex;
            const id = 'r-' + localIndex;
            rowKey += id;

            return (
              <Recipient id={id} label={item.name} key={id}>
                {
                  <input
                    type="checkbox"
                    checked={alertedUsers.has(item.profile_id)}
                    onChange={this.handleRecipientChange}
                    value={localIndex}
                    id={id}
                  />
                }
              </Recipient>
            );
          }

          return <Recipient.Empty />;
        });

        acc.push(<tr key={rowKey}>{rowItems}</tr>);
      }
      return acc;
    }, []);

    return rows;
  }

  renderChangeStatus(changeStatus: AsyncValueType<*>) {
    if (changeStatus instanceof Error) {
      return <Alert>An error occurred while saving your changes.</Alert>;
    }

    if (changeStatus === AsyncValues.Complete) {
      return <Alert>Your changes have been saved.</Alert>;
    }

    return null;
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      prevProps.changeStatus === AsyncValues.Complete &&
      this.props.changeStatus == null
    ) {
      this.props.onClose();
    }
  }

  handleChannelAlertSubButtonClick = (
    isBatchOptionSelected: boolean,
    e: SyntheticEvent<HTMLInputElement>
  ) => {
    this.setState({ batch: isBatchOptionSelected });
  };

  render() {
    const { title, onClose, userProfiles, changeStatus } = this.props;
    const { alertedUsers } = this.state;
    const allRecipientsSelected = alertedUsers.count() === userProfiles.length;
    const modalBodyContent =
      userProfiles.length === 0 ? (
        <div className="ph4 pv6 tc relative gray" style={{ width: '55em' }}>
          Could not find any users with permissions to receive alerts for this
          channel.
        </div>
      ) : (
        <div className="ph4 pv3 relative" style={{ width: '55em' }}>
          {this.renderChangeStatus(changeStatus)}
          <section>
            <header>
              <h3 className="f3 ma0 mb2 brandon">Manage Users</h3>
            </header>
            <table className="w-100 collapse">
              <tbody>
                {userProfiles.length > 1 && (
                  <tr>
                    <td colSpan="2" className={Tchyns.recipientCell}>
                      <label className={Tchyns.recipientLabel}>
                        <input
                          type="checkbox"
                          checked={allRecipientsSelected}
                          onChange={this.handleBulkRecipientChange}
                        />
                        <span className="pl3 b">Select All</span>
                      </label>
                    </td>
                  </tr>
                )}
                {this.renderRecepients()}
              </tbody>
            </table>
          </section>
          <br />
          <section>
            <header>
              <h3 className="f3 ma0 mb2 brandon">Manage Alerts</h3>
            </header>
            <div className="flex flex-row">
              <p className="ma0 mr4">
                Receive email alerts for content in this channel. Choose the
                frequency of your notifications.
              </p>
              <div className="flex-none nowrap">
                <ChannelAlertSubscriptionButtons
                  isBatchOptionSelected={this.state.batch}
                  handleSubButtonClick={this.handleChannelAlertSubButtonClick}
                />
              </div>
            </div>
          </section>
        </div>
      );

    return (
      <Modal onClose={onClose}>
        <Modal.Header>{title}</Modal.Header>
        <Modal.Body>{modalBodyContent}</Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            className="input-reset bg-blue white brandon b--none b pv2 ph3 f4 br1 shadow-hover pointer grow"
            onClick={this.handleSubmit}
            disabled={
              changeStatus === AsyncValues.Pending || userProfiles.length === 0
            }
          >
            {changeStatus === AsyncValues.Pending ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

type ChannelAlertSubButtonProps = {
  isBatchOptionSelected: boolean,
  handleSubButtonClick: (boolean, SyntheticEvent<HTMLInputElement>) => void,
};

class ChannelAlertSubscriptionButtons extends React.PureComponent<ChannelAlertSubButtonProps> {
  static buttonData = [
    { buttonLabel: 'Individual Alerts', isBatchOption: false },
    { buttonLabel: 'Daily Batches', isBatchOption: true },
  ];

  render() {
    const { isBatchOptionSelected } = this.props;
    const buttons = ChannelAlertSubscriptionButtons.buttonData.map(button => {
      const { isBatchOption, buttonLabel } = button;
      const isActive = isBatchOptionSelected === isBatchOption;

      const className = classnames(
        'input-reset button-reset bg-animate pointer br1 f6 ph3 ba pv2 hover-shadow',
        {
          'bg-blue o-80 white': isActive,
          'bg-white blue hover-dark-blue': !isActive,
        }
      );

      return (
        <button
          key={buttonLabel}
          className={className}
          onClick={e => this.props.handleSubButtonClick(isBatchOption, e)}
        >
          {buttonLabel}
        </button>
      );
    });

    return <ButtonGroup>{buttons}</ButtonGroup>;
  }
}

export default ChannelAlert;



// WEBPACK FOOTER //
// ./src/containers/channels/ChannelAlert.js